import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useUsers } from '../hooks/useUsers';
import { useAdminChatUsers } from '../hooks/useAdminChatUsers';
import { usePartnerRequests } from '../hooks/usePartnerRequests';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { supabase } from '../services/supabase';
import { appConfigService } from '../services/appConfigService';
import { securityErrorToMetadata, securityMonitorService } from '../services/securityMonitorService';
import {
  Permission,
  ROLE_OPTIONS,
  ROLE_PERMISSIONS,
  getRoleLabel,
  hasAdminRole,
  hasPermission,
  hasSuperAdminRole,
  normalizeRoleKeys,
} from '../utils/roles';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const MIAAlert = styled.div`
  padding: 0.35rem 0.6rem;
  border-radius: 999px;
  background: var(--warningContainer);
  color: var(--onWarningContainer);
  font-size: 0.8rem;
  font-weight: 600;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const LegendButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--primary);
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const MessagesLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--primary);
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const MessagesPill = styled.span`
  margin-left: 0.25rem;
  background: var(--error);
  color: var(--surface);
  border-radius: 9999px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  min-width: 18px;
  text-align: center;
`;

const ToastRegion = styled.div`
  position: fixed;
  top: 5rem;
  right: 1.25rem;
  z-index: 220;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
  pointer-events: none;
`;

const Toast = styled.div`
  padding: 0.9rem 1.1rem;
  border-radius: 10px;
  background: var(--primary);
  color: var(--surface);
  font-weight: 600;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
`;

const MenuWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const MenuButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--primary);
  padding: 0.35rem 0.6rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const MenuPopover = styled.div`
  position: absolute;
  top: 42px;
  right: 0;
  min-width: 140px;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 10px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
  padding: 0.4rem;
  z-index: 220;
`;

const MenuItem = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  color: var(--onSurface);
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    background: var(--surfaceVariant);
    color: var(--primary);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const LegendIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid currentColor;
  font-size: 0.75rem;
  line-height: 1;
`;

const ControlsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 2fr) minmax(180px, 1fr) minmax(180px, 1fr) auto auto;
  gap: 2rem;
  margin-bottom: 1rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const BulkActionStrip = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surfaceVariant);
  padding: 0.75rem 0.9rem;
  margin-bottom: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const BulkActionText = styled.p`
  margin: 0;
  color: var(--onSurface);
  font-size: 0.85rem;
  font-weight: 600;
`;

const BulkRoleGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const BulkRoleColumn = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surfaceVariant);
  padding: 0.9rem;
  display: grid;
  gap: 0.65rem;
`;

const BulkRoleTitle = styled.h3`
  margin: 0;
  font-size: 0.9rem;
  color: var(--onSurface);
`;

const RowSelectCell = styled.td`
  width: 44px;
  text-align: center;
`;

const RowSelectHeader = styled.th`
  width: 44px;
  text-align: center;
`;

const HelperText = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.85rem;
`;

const Label = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.85rem;
  font-weight: 600;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  font-size: 0.9rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  font-size: 0.9rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  font-size: 0.9rem;
  resize: vertical;
  font-family: inherit;
`;

const OnlineToggle = styled.button<{ $active?: boolean }>`
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--success)' : 'var(--outline)')};
  background: ${({ $active }) => ($active ? 'var(--successContainer)' : 'var(--surface)')};
  color: ${({ $active }) => ($active ? 'var(--success)' : 'var(--primary)')};
  padding: 0.45rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--successContainer)' : 'var(--surfaceVariant)')};
  }
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }

  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }

  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const AvatarWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const AvatarCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--outline);
  color: var(--onSurfaceVariant);
  font-weight: 700;
  font-size: 0.8rem;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const OnlineDot = styled.span`
  position: absolute;
  right: -3px;
  bottom: -3px;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: var(--success);
  border: 2px solid var(--surface);
  box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.4);
`;

const RoleBadge = styled.span`
  background: var(--surfaceVariant);
  color: var(--primary);
  border-radius: 999px;
  padding: 2px 8px;
  margin-right: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
`;

const NewPartnerBadge = styled.span<{ $verified?: boolean }>`
  background: ${({ $verified }) => ($verified ? 'var(--successContainer)' : 'var(--warningContainer)')};
  color: ${({ $verified }) => ($verified ? 'var(--success)' : 'var(--onWarningContainer)')};
  border-radius: 999px;
  padding: 2px 8px;
  margin-right: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
`;

const NameLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const RoleEditor = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.75rem;
  align-items: center;
`;

const RoleToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  border: 1px solid ${({ $variant }) => ($variant === 'primary' ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $variant }) => ($variant === 'primary' ? 'var(--primary)' : 'transparent')};
  color: ${({ $variant }) => ($variant === 'primary' ? 'var(--surface)' : 'var(--primary)')};
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 200;
`;

const ModalCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  width: min(920px, 96vw);
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--outline);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 1.1rem;
  cursor: pointer;
  color: var(--onSurfaceVariant);
`;

const ModalBody = styled.div`
  padding: 1.25rem;
  overflow: auto;
`;

const LinkGrid = styled.div`
  display: grid;
  gap: 1.25rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  margin-top: 1rem;
`;

const LinkColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const LinkCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 0.75rem;
  background: var(--surfaceVariant);
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const CampusGrid = styled.div`
  display: grid;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const CampusRow = styled.div`
  display: grid;
  gap: 2.5rem;
  grid-template-columns: minmax(140px, 1.3fr) repeat(3, minmax(110px, 1fr)) auto;
  align-items: center;

  @media (max-width: 820px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const LegendGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

const LegendRoleCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 0.9rem 1rem;
  background: var(--surfaceVariant);
`;

const LegendRoleTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 0.95rem;
  color: var(--onSurface);
`;

const LegendList = styled.ul`
  margin: 0;
  padding-left: 1.2rem;
  color: var(--onSurfaceVariant);
  font-size: 0.85rem;
`;

const LegendEmpty = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const UploadCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1rem;
  background: var(--surfaceVariant);
  display: grid;
  gap: 0.85rem;
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const UploadFileName = styled.span`
  font-weight: 600;
  color: var(--onSurface);
  font-size: 0.9rem;
`;

const UploadErrorList = styled.ul`
  margin: 0;
  padding-left: 1.2rem;
  color: var(--error);
  font-size: 0.85rem;
`;

const UploadPreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.5rem 0.65rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.85rem;
    color: var(--onSurfaceVariant);
  }

  th {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
`;

const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1rem;
  color: var(--onSurfaceVariant);
  font-size: 0.8rem;
  border-top: 1px solid var(--outline);
  background: var(--surface);
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  min-width: 32px;
  height: 32px;
  padding: 0 0.55rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $active }) => ($active ? 'var(--surface)' : 'var(--onSurfaceVariant)')};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--primaryDark)' : 'var(--surfaceVariant)')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageEllipsis = styled.span`
  padding: 0 0.4rem;
  color: var(--onSurfaceVariant);
  font-weight: 600;
`;

const permissionLabels: Record<Permission, string> = {
  [Permission.VIEW_DASHBOARD]: 'Dashboard access',
  [Permission.VIEW_MARKETING]: 'Access marketing dashboard tools',
  [Permission.VIEW_ANNOUNCEMENTS]: 'View announcements',
  [Permission.MANAGE_CONTENT]: 'Manage announcements, sermons, devotionals, declarations',
  [Permission.VIEW_SERMON_CONTENT]: 'View sermon series and sermons',
  [Permission.VIEW_SERMON_QUESTIONS]: 'View sermon questions',
  [Permission.MANAGE_EVENTS]: 'Manage events',
  [Permission.SUBMIT_EVENT_REQUESTS]: 'Submit event requests only',
  [Permission.MODERATE_COMMUNITY]: 'Moderate posts, prayers, groups',
  [Permission.MANAGE_VOLUNTEERS]: 'Manage volunteers and partner requests',
  [Permission.VIEW_PARTNER_REQUESTS]: 'View partner requests',
  [Permission.MANAGE_KIDS_CHURCH]: 'Manage Kids Church',
  [Permission.VIEW_USERS]: 'View users and member profiles',
  [Permission.MANAGE_USER_ROLES]: 'Assign and update roles',
  [Permission.VIEW_SUPER_ADMIN]: 'Access Super Admin tools',
  [Permission.VIEW_GROWTH]: 'Local Engagement (Growth) dashboard',
  [Permission.VIEW_GROUP_GOALS]: 'View group goals analytics',
  [Permission.MANAGE_SERMON_QUESTIONS]: 'Moderate live sermon questions and queue',
  [Permission.VIEW_FINANCE]: 'Access finance dashboard and reports',
  [Permission.VIEW_TRANSPORTATION_REQUESTS]: 'View transportation ride requests',
  [Permission.VIEW_CHURCH_PLANNER]: 'Access Church Planner workspace',
  [Permission.CREATE_CHURCH_PLAN]: 'Create church service plans',
  [Permission.MANAGE_CHURCH_PLANNER_ROLES]: 'Manage church planner role definitions',
  [Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS]: 'Manage church planner assignments',
  [Permission.MANAGE_FACILITIES]: 'Manage facility requests and blackout calendar',
};

const ONLINE_WINDOW_MS = 5 * 60 * 1000;
const PAGE_SIZE = 100;

type CsvUserRow = {
  rowNumber: number;
  fullName: string;
  email: string | null;
  phone: string | null;
  memberSince: string | null;
  householdAddress: string | null;
};

type PushDestination =
  | 'notifications'
  | 'events'
  | 'transportation'
  | 'home_announcements'
  | 'give'
  | 'social'
  | 'confessions'
  | 'kids_church'
  | 'find_your_fit'
  | 'become_partner'
  | 'businesses'
  | 'request_ride'
  | 'book_store';
type PushRideStatus = 'pending' | 'assigned' | 'in_route' | 'completed' | 'cancelled';

const PUSH_DESTINATION_OPTIONS: Array<{ value: PushDestination; label: string }> = [
  { value: 'notifications', label: 'Notifications screen' },
  { value: 'give', label: 'Give screen' },
  { value: 'social', label: 'Social screen' },
  { value: 'confessions', label: 'Confessions screen' },
  { value: 'kids_church', label: 'Kids Church screen' },
  { value: 'find_your_fit', label: 'Find Your Fit screen' },
  { value: 'become_partner', label: 'Become a Partner screen' },
  { value: 'businesses', label: 'Business screen' },
  { value: 'request_ride', label: 'Request Ride screen' },
  { value: 'book_store', label: 'Book Store screen' },
  { value: 'events', label: 'Events' },
  { value: 'transportation', label: 'Transportation requests' },
  { value: 'home_announcements', label: 'Home announcements' },
];

const PUSH_RIDE_STATUS_OPTIONS: Array<{ value: PushRideStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_route', label: 'In route' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

type CsvParseResult = {
  rows: CsvUserRow[];
  errors: string[];
};

type BulkProvisionResult = {
  created: number;
  skipped: number;
  failed: number;
  errors?: { row: number; message: string }[];
};

type ContactEditMode = 'email' | 'phone';

type ContactEditTarget = {
  id: string;
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
};

const validateAdminPassword = (value: string) => {
  if (value.length < 10) return 'Password must be at least 10 characters.';
  if (!/[a-z]/.test(value)) return 'Password must include a lowercase letter.';
  if (!/[A-Z]/.test(value)) return 'Password must include an uppercase letter.';
  if (!/\d/.test(value)) return 'Password must include a number.';
  if (!/[^A-Za-z0-9]/.test(value)) return 'Password must include a symbol.';
  return null;
};

const normalizeCsvHeader = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const parseCsvText = (raw: string): string[][] => {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentValue = '';
  let inQuotes = false;

  for (let i = 0; i < raw.length; i += 1) {
    const char = raw[i];
    const nextChar = raw[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentValue += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      currentRow.push(currentValue);
      currentValue = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        i += 1;
      }
      currentRow.push(currentValue);
      if (currentRow.some((cell) => cell.trim() !== '')) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentValue = '';
      continue;
    }

    currentValue += char;
  }

  if (currentValue.length || currentRow.length) {
    currentRow.push(currentValue);
    if (currentRow.some((cell) => cell.trim() !== '')) {
      rows.push(currentRow);
    }
  }

  return rows;
};

const normalizePhoneInput = (value: string) => {
  const cleaned = value.replace(/[^\d+]/g, '');
  if (!cleaned) return null;
  const digits = cleaned.replace(/\D/g, '');
  const candidate = cleaned.startsWith('+')
    ? cleaned
    : digits.length === 10
      ? `+1${digits}`
      : `+${digits}`;
  return /^\+[1-9]\d{6,14}$/.test(candidate) ? candidate : null;
};

const normalizeMemberSince = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}$/.test(trimmed)) return `${trimmed}-01`;
  if (/^\d{4}$/.test(trimmed)) return `${trimmed}-01-01`;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().split('T')[0];
};

const buildCsvRows = (raw: string): CsvParseResult => {
  const rows = parseCsvText(raw);
  if (!rows.length) {
    return { rows: [], errors: ['No rows found in the CSV file.'] };
  }

  const headerRow = rows[0].map((header) => normalizeCsvHeader(header));
  const headerLookup = (aliases: string[]) =>
    headerRow.findIndex((header) => aliases.includes(header));

  const nameIndex = headerLookup(['name', 'full name', 'full_name', 'member name']);
  const emailIndex = headerLookup(['email', 'email address', 'e mail', 'email_address']);
  const phoneIndex = headerLookup(['phone', 'phone number', 'mobile', 'mobile number']);
  const memberSinceIndex = headerLookup(['member since', 'member_since', 'membership date', 'member date']);
  const householdIndex = headerLookup(['household address', 'address', 'home address', 'street address']);

  if (nameIndex < 0) {
    return { rows: [], errors: ['Missing required column: name.'] };
  }

  const parsedRows: CsvUserRow[] = [];
  const errors: string[] = [];
  const emailSet = new Set<string>();
  const phoneSet = new Set<string>();

  rows.slice(1).forEach((row, index) => {
    const rowNumber = index + 2;
    const rawName = row[nameIndex] ?? '';
    const rawEmail = emailIndex >= 0 ? row[emailIndex] ?? '' : '';
    const rawPhone = phoneIndex >= 0 ? row[phoneIndex] ?? '' : '';
    const rawMemberSince = memberSinceIndex >= 0 ? row[memberSinceIndex] ?? '' : '';
    const rawHousehold = householdIndex >= 0 ? row[householdIndex] ?? '' : '';

    const fullName = rawName.trim();
    const normalizedEmail = rawEmail.trim().toLowerCase();
    const email = normalizedEmail && /^\S+@\S+\.\S+$/.test(normalizedEmail) ? normalizedEmail : null;
    const phone = rawPhone.trim() ? normalizePhoneInput(rawPhone.trim()) : null;
    const memberSince = rawMemberSince.trim() ? normalizeMemberSince(rawMemberSince) : null;
    const householdAddress = rawHousehold.trim() ? rawHousehold.trim() : null;

    if (!fullName) {
      errors.push(`Row ${rowNumber}: name is required.`);
      return;
    }

    if (!email && !phone) {
      errors.push(`Row ${rowNumber}: provide a valid email or phone number.`);
      return;
    }

    if (normalizedEmail && !email) {
      errors.push(`Row ${rowNumber}: email address is invalid.`);
      return;
    }

    if (rawPhone.trim() && !phone) {
      errors.push(`Row ${rowNumber}: phone number is invalid.`);
      return;
    }

    if (memberSinceIndex >= 0 && rawMemberSince.trim() && !memberSince) {
      errors.push(`Row ${rowNumber}: member since date is invalid.`);
      return;
    }

    if (email && emailSet.has(email)) {
      errors.push(`Row ${rowNumber}: duplicate email in CSV.`);
      return;
    }
    if (phone && phoneSet.has(phone)) {
      errors.push(`Row ${rowNumber}: duplicate phone in CSV.`);
      return;
    }

    if (email) emailSet.add(email);
    if (phone) phoneSet.add(phone);

    parsedRows.push({
      rowNumber,
      fullName,
      email,
      phone,
      memberSince,
      householdAddress,
    });
  });

  return { rows: parsedRows, errors };
};

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [gapFilter, setGapFilter] = useState<'all' | 'overdue'>('all');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [recentSort, setRecentSort] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [showBulkRoleModal, setShowBulkRoleModal] = useState(false);
  const [bulkAddRoles, setBulkAddRoles] = useState<string[]>([]);
  const [bulkRemoveRoles, setBulkRemoveRoles] = useState<string[]>([]);
  const [bulkRoleSaving, setBulkRoleSaving] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [draftRoles, setDraftRoles] = useState<string[]>([]);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const [visitGapDays, setVisitGapDays] = useState(60);
  const [visitGapSaving, setVisitGapSaving] = useState(false);
  const [visitGapError, setVisitGapError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [actionMenuUserId, setActionMenuUserId] = useState<string | null>(null);
  const [showVisitGapModal, setShowVisitGapModal] = useState(false);
  const [showCampusesModal, setShowCampusesModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPushModal, setShowPushModal] = useState(false);
  const [pushRecipientIds, setPushRecipientIds] = useState<string[]>([]);
  const [pushTitle, setPushTitle] = useState('');
  const [pushMessage, setPushMessage] = useState('');
  const [pushDestination, setPushDestination] = useState<PushDestination>('notifications');
  const [pushEventId, setPushEventId] = useState('');
  const [pushRideStatus, setPushRideStatus] = useState<PushRideStatus>('pending');
  const [pushSubmitting, setPushSubmitting] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [contactEditTarget, setContactEditTarget] = useState<ContactEditTarget | null>(null);
  const [contactEditMode, setContactEditMode] = useState<ContactEditMode>('email');
  const [contactEditValue, setContactEditValue] = useState('');
  const [contactEditSaving, setContactEditSaving] = useState(false);
  const [contactEditError, setContactEditError] = useState<string | null>(null);
  const [passwordEditTarget, setPasswordEditTarget] = useState<ContactEditTarget | null>(null);
  const [passwordDraft, setPasswordDraft] = useState('');
  const [passwordConfirmDraft, setPasswordConfirmDraft] = useState('');
  const [passwordEditError, setPasswordEditError] = useState<string | null>(null);
  const [passwordEditSaving, setPasswordEditSaving] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const csvInputRef = useRef<HTMLInputElement | null>(null);
  const [lastVisitByUser, setLastVisitByUser] = useState<Record<string, string | null>>({});
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [sendingPasswordResetUserId, setSendingPasswordResetUserId] = useState<string | null>(null);
  const [campuses, setCampuses] = useState<
    { id: string; name: string; latitude: number; longitude: number; radius_meters: number; is_active: boolean }[]
  >([]);
  const [campusForm, setCampusForm] = useState({
    name: '',
    latitude: '',
    longitude: '',
    radius_meters: '150',
  });
  const [linkPrimaryId, setLinkPrimaryId] = useState('');
  const [linkSecondaryId, setLinkSecondaryId] = useState('');
  const [linkPrimarySearch, setLinkPrimarySearch] = useState('');
  const [linkSecondarySearch, setLinkSecondarySearch] = useState('');
  const [linkTransferContact, setLinkTransferContact] = useState(true);
  const [linkDeactivateSecondary, setLinkDeactivateSecondary] = useState(true);
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [primaryLinkOptions, setPrimaryLinkOptions] = useState<
    { id: string; full_name?: string | null; email?: string | null; phone?: string | null }[]
  >([]);
  const [secondaryLinkOptions, setSecondaryLinkOptions] = useState<
    { id: string; full_name?: string | null; email?: string | null; phone?: string | null }[]
  >([]);
  const [linkSelectedById, setLinkSelectedById] = useState<
    Record<string, { id: string; full_name?: string | null; email?: string | null; phone?: string | null }>
  >({});
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [csvRows, setCsvRows] = useState<CsvUserRow[]>([]);
  const [csvErrors, setCsvErrors] = useState<string[]>([]);
  const [csvParsing, setCsvParsing] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState<BulkProvisionResult | null>(null);

  const { data: usersResult, isLoading, error } = useUsers({
    page: currentPage,
    pageSize: PAGE_SIZE,
    search,
    roleFilter,
    onlineOnly,
    orderByRecent: recentSort,
  });
  const users = useMemo(() => usersResult?.users ?? [], [usersResult?.users]);
  const totalCount = useMemo(() => usersResult?.totalCount ?? 0, [usersResult?.totalCount]);

  const { data: partnerRequests } = usePartnerRequests();
  const { data: adminChatUsers = [] } = useAdminChatUsers(user?.id ?? '');
  const adminUnreadCount = useMemo(
    () => adminChatUsers.reduce((sum, chatUser) => sum + (chatUser.unreadCount ?? 0), 0),
    [adminChatUsers]
  );
  const { data: profile } = useCurrentUserProfile(user?.id);

  const partnerRequestStatusByUser = useMemo(() => {
    const statusByUser = new Map<string, boolean>();
    partnerRequests?.forEach((request) => {
      if (request.user_id) {
        statusByUser.set(request.user_id, Boolean(request.verified));
      }
    });
    return statusByUser;
  }, [partnerRequests]);

  const currentRoles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageRoles = hasPermission(currentRoles, Permission.MANAGE_USER_ROLES);
  const canManageSuperAdminRole = hasSuperAdminRole(currentRoles);
  const canManageVisitGap = hasAdminRole(currentRoles);
  const canDeleteUsers = hasSuperAdminRole(currentRoles);
  const canEditUserContact = hasAdminRole(currentRoles);
  const canManageUserPasswords = hasAdminRole(currentRoles);
  const canLinkAccounts = hasAdminRole(currentRoles);
  const canBulkProvision = hasAdminRole(currentRoles);
  const canSendPushNotifications = hasAdminRole(currentRoles);
  const canMessageUsers = Boolean(user?.id);

  useEffect(() => {
    const loadVisitGap = async () => {
      const result = await appConfigService.getConfiguration<number>('campus_visit_gap_days');
      if (result.success && result.data != null) {
        const parsed = Number(result.data);
        if (Number.isFinite(parsed) && parsed > 0) {
          setVisitGapDays(parsed);
          return;
        }
      }
      setVisitGapDays(60);
    };

    loadVisitGap();
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const showToast = useCallback((message: string) => {
    setToastMessage(message);
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  }, []);

  const logUsersSecurityEvent = useCallback(
    (payload: Parameters<typeof securityMonitorService.logClientSecurityEvent>[0]) => {
      void securityMonitorService.logClientSecurityEvent(payload);
    },
    []
  );

  const loadCampuses = useCallback(async () => {
    const { data: campusRows, error: campusError } = await supabase
      .from('campuses')
      .select('id, name, latitude, longitude, radius_meters, is_active')
      .order('name', { ascending: true });
    if (campusError) {
      showToast('Failed to load campuses.');
      return;
    }
    setCampuses((campusRows as any[]) || []);
  }, [showToast]);

  const handleAddCampus = async () => {
    const name = campusForm.name.trim();
    const latitude = Number(campusForm.latitude);
    const longitude = Number(campusForm.longitude);
    const radius = Math.max(1, Math.round(Number(campusForm.radius_meters || '150')));
    if (!name || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      showToast('Enter a name, latitude, and longitude.');
      return;
    }
    const { error } = await supabase
      .from('campuses')
      .insert({
        name,
        latitude,
        longitude,
        radius_meters: radius,
        is_active: true,
      });
    if (error) {
      showToast('Failed to add campus.');
      return;
    }
    setCampusForm({ name: '', latitude: '', longitude: '', radius_meters: '150' });
    await loadCampuses();
    showToast('Campus added.');
  };

  const handleSaveCampus = async (campusId: string) => {
    const target = campuses.find((item) => item.id === campusId);
    if (!target) return;
    const radius = Math.max(1, Math.round(Number(target.radius_meters || 150)));
    const { error } = await supabase
      .from('campuses')
      .update({
        name: target.name.trim(),
        latitude: target.latitude,
        longitude: target.longitude,
        radius_meters: radius,
        is_active: target.is_active,
        updated_at: new Date().toISOString(),
      })
      .eq('id', campusId);
    if (error) {
      showToast('Failed to save campus.');
      return;
    }
    await loadCampuses();
    showToast('Campus updated.');
  };

  useEffect(() => {
    const loadLastVisits = async () => {
      if (!users.length) {
        setLastVisitByUser({});
        return;
      }
      const userIds = users.map((userRow) => userRow.id);
      const { data: visits, error } = await supabase
        .from('campus_visit_counts')
        .select('user_id, last_visit_at')
        .in('user_id', userIds);
      if (error) {
        return;
      }
      const next: Record<string, string | null> = {};
      (visits ?? []).forEach((row: { user_id: string; last_visit_at?: string | null }) => {
        if (!row.user_id) return;
        if (!row.last_visit_at) return;
        const existing = next[row.user_id];
        if (!existing || new Date(row.last_visit_at).getTime() > new Date(existing).getTime()) {
          next[row.user_id] = row.last_visit_at;
        }
      });
      setLastVisitByUser(next);
    };

    loadLastVisits();
  }, [users]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(event.target as Node)) return;
      setMenuOpen(false);
    };
    window.addEventListener('mousedown', handleClick);
    return () => {
      window.removeEventListener('mousedown', handleClick);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!actionMenuUserId) return;
    const handleClick = (event: MouseEvent) => {
      if (!actionMenuRef.current) return;
      if (actionMenuRef.current.contains(event.target as Node)) return;
      setActionMenuUserId(null);
    };
    window.addEventListener('mousedown', handleClick);
    return () => {
      window.removeEventListener('mousedown', handleClick);
    };
  }, [actionMenuUserId]);

  useEffect(() => {
    if (!showCampusesModal) return;
    loadCampuses();
  }, [showCampusesModal, loadCampuses]);

  useEffect(() => {
    if (showLinkModal) return;
    setLinkPrimaryId('');
    setLinkSecondaryId('');
    setLinkPrimarySearch('');
    setLinkSecondarySearch('');
    setLinkTransferContact(true);
    setLinkDeactivateSecondary(true);
    setLinkSaving(false);
    setLinkError(null);
    setPrimaryLinkOptions([]);
    setSecondaryLinkOptions([]);
    setLinkSelectedById({});
  }, [showLinkModal]);

  const roleOptions = useMemo(() => ROLE_OPTIONS.map((role) => role.value), []);

  const hasOverdueUsers = useMemo(() => {
    const gapDays = Number.isFinite(visitGapDays) && visitGapDays > 0 ? visitGapDays : 60;
    const gapMs = gapDays * 24 * 60 * 60 * 1000;
    return Object.values(lastVisitByUser).some((lastVisit) => {
      if (!lastVisit) return false;
      const lastVisitMs = new Date(lastVisit).getTime();
      if (!Number.isFinite(lastVisitMs)) return false;
      return Date.now() - lastVisitMs > gapMs;
    });
  }, [lastVisitByUser, visitGapDays]);

  const isUserOverGap = useCallback((userId: string) => {
    const lastVisit = lastVisitByUser[userId];
    if (!lastVisit) return false;
    const lastVisitMs = new Date(lastVisit).getTime();
    if (!Number.isFinite(lastVisitMs)) return false;
    const gapDays = Number.isFinite(visitGapDays) && visitGapDays > 0 ? visitGapDays : 60;
    const gapMs = gapDays * 24 * 60 * 60 * 1000;
    return Date.now() - lastVisitMs > gapMs;
  }, [lastVisitByUser, visitGapDays]);

  const isUserOnline = useCallback((lastActive?: string | number | null) => {
    if (lastActive == null) return false;
    // Support ISO string or Unix timestamp (seconds or ms)
    const lastActiveMs =
      typeof lastActive === 'number'
        ? lastActive < 1e12
          ? lastActive * 1000
          : lastActive
        : new Date(lastActive).getTime();
    if (!Number.isFinite(lastActiveMs)) return false;
    return Date.now() - lastActiveMs <= ONLINE_WINDOW_MS;
  }, []);

  const fetchLinkOptions = useCallback(async (searchValue: string) => {
    const normalized = searchValue.trim();
    let query = supabase
      .from('users_directory')
      .select('id, full_name, email, phone')
      .order('full_name', { ascending: true })
      .limit(20);

    if (normalized) {
      query = query.or(
        `full_name.ilike.%${normalized}%,email.ilike.%${normalized}%,phone.ilike.%${normalized}%`
      );
    }

    const { data: rows, error } = await query;
    if (error) return [];
    return (rows ?? []) as { id: string; full_name?: string | null; email?: string | null; phone?: string | null }[];
  }, []);

  useEffect(() => {
    if (!showLinkModal) return;
    let isActive = true;

    const loadPrimaryOptions = async () => {
      const rows = await fetchLinkOptions(linkPrimarySearch);
      if (!isActive) return;
      const filtered = rows.filter((row) => row.id !== linkSecondaryId);
      setPrimaryLinkOptions(filtered);
      setLinkSelectedById((prev) => {
        const next = { ...prev };
        filtered.forEach((row) => {
          next[row.id] = row;
        });
        return next;
      });
    };

    loadPrimaryOptions();
    return () => {
      isActive = false;
    };
  }, [fetchLinkOptions, linkPrimarySearch, linkSecondaryId, showLinkModal]);

  useEffect(() => {
    if (!showLinkModal) return;
    let isActive = true;

    const loadSecondaryOptions = async () => {
      const rows = await fetchLinkOptions(linkSecondarySearch);
      if (!isActive) return;
      const filtered = rows.filter((row) => row.id !== linkPrimaryId);
      setSecondaryLinkOptions(filtered);
      setLinkSelectedById((prev) => {
        const next = { ...prev };
        filtered.forEach((row) => {
          next[row.id] = row;
        });
        return next;
      });
    };

    loadSecondaryOptions();
    return () => {
      isActive = false;
    };
  }, [fetchLinkOptions, linkSecondarySearch, linkPrimaryId, showLinkModal]);

  const filteredUsers = useMemo(() => {
    let next = users;
    if (gapFilter === 'overdue') {
      next = next.filter((userRow) => isUserOverGap(userRow.id));
    }
    return next;
  }, [users, gapFilter, isUserOverGap]);

  const selectableUsers = useMemo(
    () => (canManageRoles ? filteredUsers.filter((userRow) => userRow.id !== user?.id) : []),
    [canManageRoles, filteredUsers, user?.id]
  );

  const selectedUsersOnPage = useMemo(
    () => filteredUsers.filter((userRow) => selectedUserIds.includes(userRow.id)),
    [filteredUsers, selectedUserIds]
  );

  const visibleUserIdSet = useMemo(() => new Set(filteredUsers.map((userRow) => userRow.id)), [filteredUsers]);
  const visibleUserById = useMemo(
    () => new Map(filteredUsers.map((userRow) => [userRow.id, userRow])),
    [filteredUsers]
  );
  const pushRecipients = useMemo(
    () => pushRecipientIds.map((id) => visibleUserById.get(id)).filter(Boolean),
    [pushRecipientIds, visibleUserById]
  );

  const selectableUserIdSet = useMemo(() => new Set(selectableUsers.map((userRow) => userRow.id)), [selectableUsers]);

  const allSelectableSelected =
    selectableUsers.length > 0 && selectableUsers.every((userRow) => selectedUserIds.includes(userRow.id));
  const someSelectableSelected = !allSelectableSelected && selectedUserIds.length > 0;

  const hasBulkRoleChanges = bulkAddRoles.length > 0 || bulkRemoveRoles.length > 0;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)),
    [totalCount]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, roleFilter, gapFilter, recentSort, onlineOnly]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setSelectedUserIds([]);
  }, [search, roleFilter, gapFilter, recentSort, onlineOnly, currentPage]);

  useEffect(() => {
    setSelectedUserIds((prev) => prev.filter((userId) => selectableUserIdSet.has(userId)));
  }, [selectableUserIdSet]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someSelectableSelected;
  }, [someSelectableSelected]);

  useEffect(() => {
    if (!showBulkRoleModal) return;
    if (selectedUserIds.length > 0) return;
    setShowBulkRoleModal(false);
    setBulkAddRoles([]);
    setBulkRemoveRoles([]);
  }, [showBulkRoleModal, selectedUserIds.length]);

  useEffect(() => {
    if (!showPushModal) return;
    setPushRecipientIds((prev) => prev.filter((userId) => visibleUserIdSet.has(userId)));
  }, [showPushModal, visibleUserIdSet]);

  const pageIndicators = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const indicators: Array<number | 'ellipsis'> = [1];
    const start = Math.max(2, currentPage - 2);
    const end = Math.min(totalPages - 1, currentPage + 2);

    if (start > 2) {
      indicators.push('ellipsis');
    }

    for (let page = start; page <= end; page += 1) {
      indicators.push(page);
    }

    if (end < totalPages - 1) {
      indicators.push('ellipsis');
    }

    indicators.push(totalPages);
    return indicators;
  }, [currentPage, totalPages]);

  const selectedPrimary = linkPrimaryId ? linkSelectedById[linkPrimaryId] ?? null : null;
  const selectedSecondary = linkSecondaryId ? linkSelectedById[linkSecondaryId] ?? null : null;

  const getUserLabel = (userRow: { full_name?: string | null; email?: string | null; phone?: string | null }) => {
    const name = userRow.full_name || 'Unnamed member';
    const contact = userRow.email || userRow.phone || 'No contact';
    return `${name} • ${contact}`;
  };

  const normalizeSelection = (roles: string[]) => {
    const unique = Array.from(new Set(roles));
    const hasVisitor = unique.includes('visitor');
    const filtered = unique.filter((role) => role !== 'visitor');
    if (filtered.length > 0) {
      return filtered;
    }
    return hasVisitor || unique.length === 0 ? ['visitor'] : unique;
  };

  const canEditManagedRole = useCallback(
    (roleValue: string) => roleValue !== 'super_admin' || canManageSuperAdminRole,
    [canManageSuperAdminRole]
  );

  const sanitizeManagedRoles = useCallback(
    (nextRolesInput: string[], currentTargetRoles?: string[] | null) => {
      const nextRoles = normalizeSelection(normalizeRoleKeys(nextRolesInput));
      if (canManageSuperAdminRole) {
        return nextRoles;
      }

      const currentTargetRoleSet = new Set(normalizeRoleKeys(currentTargetRoles ?? []));
      const sanitizedRoles = nextRoles.filter((role) => role !== 'super_admin');

      if (currentTargetRoleSet.has('super_admin')) {
        sanitizedRoles.unshift('super_admin');
      }

      return normalizeSelection(sanitizedRoles);
    },
    [canManageSuperAdminRole]
  );

  const toggleUserSelection = (userId: string) => {
    if (!canManageRoles || userId === user?.id || bulkRoleSaving) return;
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((currentId) => currentId !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAllVisible = () => {
    if (!canManageRoles || bulkRoleSaving) return;
    setSelectedUserIds((prev) => {
      if (allSelectableSelected) {
        return prev.filter((id) => !selectableUserIdSet.has(id));
      }
      const next = new Set(prev);
      selectableUsers.forEach((userRow) => {
        next.add(userRow.id);
      });
      return Array.from(next);
    });
  };

  const openBulkRoleEditor = () => {
    if (!selectedUserIds.length || !canManageRoles) return;
    setBulkAddRoles([]);
    setBulkRemoveRoles([]);
    setShowBulkRoleModal(true);
  };

  const resetPushComposer = () => {
    setShowPushModal(false);
    setPushRecipientIds([]);
    setPushTitle('');
    setPushMessage('');
    setPushDestination('notifications');
    setPushEventId('');
    setPushRideStatus('pending');
    setPushSubmitting(false);
    setPushError(null);
  };

  const openPushComposer = (recipientIds: string[]) => {
    if (!canSendPushNotifications) return;
    const uniqueRecipientIds = Array.from(new Set(recipientIds.filter((recipientId) => visibleUserIdSet.has(recipientId))));
    if (!uniqueRecipientIds.length) {
      showToast('Select at least one visible user.');
      return;
    }
    setPushRecipientIds(uniqueRecipientIds);
    setPushTitle('');
    setPushMessage('');
    setPushDestination('notifications');
    setPushEventId('');
    setPushRideStatus('pending');
    setPushError(null);
    setShowPushModal(true);
  };

  const handleSendPush = async () => {
    if (!canSendPushNotifications) return;
    const title = pushTitle.trim();
    const message = pushMessage.trim();
    const recipientIds = Array.from(new Set(pushRecipientIds.filter((recipientId) => visibleUserIdSet.has(recipientId))));

    if (!recipientIds.length) {
      setPushError('Select at least one visible recipient.');
      return;
    }
    if (!title) {
      setPushError('Title is required.');
      return;
    }
    if (!message) {
      setPushError('Message is required.');
      return;
    }
    if (!pushDestination) {
      setPushError('Landing destination is required.');
      return;
    }

    const destinationData: Record<string, unknown> = {
      type: 'wall_post',
      destination: pushDestination,
      source: 'web_admin_users_push',
      sender_id: user?.id ?? null,
      sent_at: new Date().toISOString(),
    };

    if (pushDestination === 'events') {
      const eventId = pushEventId.trim();
      if (eventId) {
        destinationData.eventId = eventId;
      }
    }
    if (pushDestination === 'transportation') {
      destinationData.status = pushRideStatus;
    }

    try {
      setPushSubmitting(true);
      setPushError(null);

      let feedErrorMessage: string | null = null;
      const { error: feedInsertError } = await supabase.from('notification_feed').insert(
        recipientIds.map((recipientId) => ({
          recipient_id: recipientId,
          sender_id: user?.id ?? null,
          type: 'wall_post',
          title,
          message,
          data: destinationData,
          read: false,
          created_at: new Date().toISOString(),
        }))
      );
      if (feedInsertError) {
        feedErrorMessage = feedInsertError.message || 'Failed to save in-app notification rows.';
      }

      const category =
        pushDestination === 'home_announcements'
          ? 'announcement'
          : pushDestination === 'events'
            ? 'events'
            : pushDestination === 'transportation'
              ? 'transportation'
              : 'general';

      let pushErrorMessage: string | null = null;
      const { data: pushData, error: pushInvokeError } = await supabase.functions.invoke(
        'send-push-notification-enhanced',
        {
          body: {
            title,
            body: message,
            data: destinationData,
            targetUsers: recipientIds,
            category,
          },
        }
      );

      if (pushInvokeError) {
        pushErrorMessage = pushInvokeError.message || 'Failed to invoke push notification function.';
      } else if (pushData && typeof pushData === 'object') {
        const payload = pushData as { success?: boolean; error?: unknown; message?: unknown };
        if (payload.success === false || payload.error) {
          pushErrorMessage = String(payload.error ?? payload.message ?? 'Push notification function returned an error.');
        }
      }

      const recipientCount = recipientIds.length;
      if (!feedErrorMessage && !pushErrorMessage) {
        showToast(`Push sent to ${recipientCount} user${recipientCount === 1 ? '' : 's'}.`);
        resetPushComposer();
        return;
      }

      const failureParts = [feedErrorMessage, pushErrorMessage].filter(Boolean);
      const summary = failureParts.join(' ');
      setPushError(summary);

      if (!feedErrorMessage && pushErrorMessage) {
        showToast('Push failed, but in-app notification rows were saved.');
      } else if (feedErrorMessage && !pushErrorMessage) {
        showToast('Push sent, but in-app notification rows failed to save.');
      } else {
        showToast('Push send failed.');
      }
    } catch (err) {
      const messageText = err instanceof Error ? err.message : 'Failed to send push notification.';
      setPushError(messageText);
      showToast('Push send failed.');
    } finally {
      setPushSubmitting(false);
    }
  };

  const closeBulkRoleEditor = () => {
    if (bulkRoleSaving) return;
    setShowBulkRoleModal(false);
    setBulkAddRoles([]);
    setBulkRemoveRoles([]);
  };

  const toggleBulkAddRole = (roleValue: string) => {
    if (bulkRoleSaving || !canEditManagedRole(roleValue)) return;
    setBulkAddRoles((prev) =>
      prev.includes(roleValue)
        ? prev.filter((existingRole) => existingRole !== roleValue)
        : [...prev, roleValue]
    );
    setBulkRemoveRoles((prev) => prev.filter((existingRole) => existingRole !== roleValue));
  };

  const toggleBulkRemoveRole = (roleValue: string) => {
    if (bulkRoleSaving || !canEditManagedRole(roleValue)) return;
    setBulkRemoveRoles((prev) =>
      prev.includes(roleValue)
        ? prev.filter((existingRole) => existingRole !== roleValue)
        : [...prev, roleValue]
    );
    setBulkAddRoles((prev) => prev.filter((existingRole) => existingRole !== roleValue));
  };

  const applyBulkRoleChanges = async () => {
    if (!canManageRoles || !selectedUserIds.length || !hasBulkRoleChanges) return;

    try {
      setBulkRoleSaving(true);
      const selectedRows = selectedUsersOnPage.filter((userRow) => selectedUserIds.includes(userRow.id));
      let skippedCount = 0;
      const updates = selectedRows
        .filter((userRow) => {
          if (userRow.id === user?.id) {
            skippedCount += 1;
            return false;
          }
          return true;
        })
        .map(async (userRow) => {
          const current = normalizeSelection(normalizeRoleKeys(userRow.roles ?? []));
          const withAdds = normalizeSelection([...current, ...bulkAddRoles]);
          const withoutRemovals = withAdds.filter((role) => !bulkRemoveRoles.includes(role));
          const nextRoles = sanitizeManagedRoles(withoutRemovals, userRow.roles ?? []);

          const { error: updateError } = await supabase
            .from('users')
            .update({ roles: nextRoles, updated_at: new Date().toISOString() })
            .eq('id', userRow.id);

          if (updateError) {
            throw updateError;
          }

          return userRow.id;
        });

      const results = await Promise.allSettled(updates);
      const updatedCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - updatedCount;

      await queryClient.invalidateQueries({ queryKey: ['users'] });

      setShowBulkRoleModal(false);
      setBulkAddRoles([]);
      setBulkRemoveRoles([]);
      setSelectedUserIds([]);

      showToast(`Bulk role update complete: ${updatedCount} updated, ${failedCount} failed, ${skippedCount} skipped.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to apply bulk role changes.';
      window.alert(message);
    } finally {
      setBulkRoleSaving(false);
    }
  };

  const startEditing = (userRow: { id: string; roles?: string[] }) => {
    setEditingUserId(userRow.id);
    setDraftRoles(normalizeSelection(normalizeRoleKeys(userRow.roles ?? [])));
    setActionMenuUserId(null);
  };

  const toggleRole = (roleValue: string) => {
    if (!canEditManagedRole(roleValue)) return;
    setDraftRoles((prev) =>
      prev.includes(roleValue)
        ? prev.filter((role) => role !== roleValue)
        : [...prev, roleValue]
    );
  };

  const cancelEditing = () => {
    setEditingUserId(null);
    setDraftRoles([]);
  };

  const saveRoles = async (userId: string) => {
    const currentTargetRoles = visibleUserById.get(userId)?.roles ?? [];
    const nextRoles = sanitizeManagedRoles(draftRoles, currentTargetRoles);
    try {
      setSavingUserId(userId);
      const { error: updateError } = await supabase
        .from('users')
        .update({ roles: nextRoles, updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (updateError) {
        throw updateError;
      }

      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditingUserId(null);
      setDraftRoles([]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update roles.';
      window.alert(message);
    } finally {
      setSavingUserId(null);
    }
  };

  const closeContactEditModal = () => {
    setContactEditTarget(null);
    setContactEditError(null);
    setContactEditSaving(false);
    setContactEditValue('');
  };

  const openContactEditor = (target: ContactEditTarget, mode: ContactEditMode) => {
    setActionMenuUserId(null);
    setContactEditTarget(target);
    setContactEditMode(mode);
    setContactEditValue(mode === 'email' ? target.email ?? '' : target.phone ?? '');
    setContactEditError(null);
  };

  const handleSaveContact = async () => {
    if (!contactEditTarget) return;
    const value = contactEditValue.trim();
    if (!value) {
      setContactEditError(`Please enter a ${contactEditMode}.`);
      return;
    }

    const body: Record<string, unknown> = { user_id: contactEditTarget.id };
    if (contactEditMode === 'email') {
      const normalizedEmail = value.toLowerCase();
      if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
        setContactEditError('Please enter a valid email address.');
        return;
      }
      body.email = normalizedEmail;
      body.email_confirm = true;
    } else {
      const normalizedPhone = normalizePhoneInput(value);
      if (!normalizedPhone) {
        setContactEditError('Please enter a valid phone number.');
        return;
      }
      body.phone = normalizedPhone;
      body.phone_confirm = true;
    }

    try {
      setContactEditSaving(true);
      setContactEditError(null);
      const { data, error } = await supabase.functions.invoke('update-user-contact', { body });
      if (error) throw error;
      if (data && typeof data === 'object' && 'error' in data) {
        throw new Error(String((data as { error?: unknown }).error ?? 'Failed to update contact.'));
      }
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      showToast(contactEditMode === 'email' ? 'User email updated.' : 'User phone updated.');
      closeContactEditModal();
    } catch (err) {
      let message = err instanceof Error ? err.message : 'Failed to update user contact.';
      if (err instanceof FunctionsHttpError && err.context?.json) {
        try {
          const responseBody = (await err.context.json()) as { error?: string };
          if (responseBody?.error) {
            message = responseBody.error;
          }
        } catch {
          // Keep default message when response body is unavailable.
        }
      }
      setContactEditError(message);
    } finally {
      setContactEditSaving(false);
    }
  };

  const closePasswordModal = () => {
    setPasswordEditTarget(null);
    setPasswordDraft('');
    setPasswordConfirmDraft('');
    setPasswordEditError(null);
    setPasswordEditSaving(false);
  };

  const openPasswordModal = (target: ContactEditTarget) => {
    setActionMenuUserId(null);
    setPasswordEditTarget(target);
    setPasswordDraft('');
    setPasswordConfirmDraft('');
    setPasswordEditError(null);
  };

  const handleSavePassword = async () => {
    if (!passwordEditTarget) return;
    const password = passwordDraft.trim();
    if (!password) {
      setPasswordEditError('Enter a temporary password.');
      return;
    }
    if (password !== passwordConfirmDraft.trim()) {
      setPasswordEditError('Password confirmation does not match.');
      return;
    }
    const validationError = validateAdminPassword(password);
    if (validationError) {
      setPasswordEditError(validationError);
      return;
    }

    try {
      setPasswordEditSaving(true);
      setPasswordEditError(null);
      const { data, error } = await supabase.functions.invoke('set-user-password', {
        body: {
          user_id: passwordEditTarget.id,
          password,
        },
      });
      if (error) throw error;
      if (data && typeof data === 'object' && 'error' in data) {
        throw new Error(String((data as { error?: unknown }).error ?? 'Failed to set password.'));
      }
      showToast('User password updated.');
      closePasswordModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set password.';
      logUsersSecurityEvent({
        eventFamily: 'account_recovery',
        eventType: 'set_user_password',
        severity: 'warning',
        outcome: 'failure',
        sourceName: 'web.super_admin.users.set_user_password',
        targetUserId: passwordEditTarget.id,
        metadata: {
          error: securityErrorToMetadata(err),
        },
      });
      setPasswordEditError(message);
    } finally {
      setPasswordEditSaving(false);
    }
  };

  const handleSaveVisitGap = async () => {
    const sanitized = Math.max(1, Math.round(visitGapDays));
    try {
      setVisitGapSaving(true);
      setVisitGapError(null);
      const result = await appConfigService.updateConfiguration(
        'campus_visit_gap_days',
        sanitized,
        'Days without a campus visit before showing inactivity warning',
        'attendance',
      );
      if (!result.success) {
        throw new Error(result.message ?? 'Failed to update visit gap.');
      }
      setVisitGapDays(sanitized);
      setShowVisitGapModal(false);
      showToast('Campus visit gap updated.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update visit gap.';
      setVisitGapError(message);
    } finally {
      setVisitGapSaving(false);
    }
  };

  const handleDeleteUser = async (target: { id: string; full_name?: string | null; email?: string | null }) => {
    if (!canDeleteUsers) return;
    if (user?.id && user.id === target.id) {
      logUsersSecurityEvent({
        eventFamily: 'admin_action',
        eventType: 'delete_user',
        severity: 'warning',
        outcome: 'blocked',
        sourceName: 'web.super_admin.users.delete_user',
        targetUserId: target.id,
        metadata: { reason: 'self_delete_blocked' },
      });
      window.alert('You cannot delete your own account.');
      return;
    }
    const displayName = target.full_name || target.email || 'this user';
    const confirmed = window.confirm(`Delete ${displayName}? This cannot be undone.`);
    if (!confirmed) return;
    try {
      setDeletingUserId(target.id);
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: { user_id: target.id },
      });
      if (error) {
        throw error;
      }
      if (data && (data as { error?: string }).error) {
        throw new Error((data as { error: string }).error);
      }
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      showToast('User deleted.');
    } catch (err) {
      let message = err instanceof Error ? err.message : 'Failed to delete user.';
      if (err instanceof FunctionsHttpError && err.context?.json) {
        try {
          const body = (await err.context.json()) as { error?: string; details?: string | null; step?: string };
          if (body?.error) {
            message = body.details ? `${body.error} ${body.details}` : body.error;
          }
        } catch {
          // Keep default message when response body is unavailable.
        }
      }
      logUsersSecurityEvent({
        eventFamily: 'admin_action',
        eventType: 'delete_user',
        severity: 'warning',
        outcome: 'failure',
        sourceName: 'web.super_admin.users.delete_user',
        targetUserId: target.id,
        metadata: {
          error: securityErrorToMetadata(err),
        },
      });
      window.alert(message);
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleSendPasswordReset = async (target: { id: string; full_name?: string | null; email?: string | null }) => {
    if (!canManageUserPasswords) return;
    const displayName = target.full_name || target.email || 'this user';
    const confirmed = window.confirm(`Send a password reset email to ${displayName}?`);
    if (!confirmed) return;
    try {
      setSendingPasswordResetUserId(target.id);
      const { data, error } = await supabase.functions.invoke('send-user-password-reset', {
        body: {
          user_id: target.id,
          redirect_to: `${window.location.origin}/reset-password`,
        },
      });
      if (error) {
        throw error;
      }
      if (data && (data as { error?: string }).error) {
        throw new Error((data as { error: string }).error);
      }
      showToast('Password reset email sent.');
    } catch (err) {
      let message = err instanceof Error ? err.message : 'Failed to send password reset email.';
      if (err instanceof FunctionsHttpError && err.context?.json) {
        try {
          const body = (await err.context.json()) as { error?: string };
          if (body?.error) {
            message = body.error;
          }
        } catch {
          // Keep default message when response body is unavailable.
        }
      }
      logUsersSecurityEvent({
        eventFamily: 'account_recovery',
        eventType: 'send_user_password_reset',
        severity: 'warning',
        outcome: 'failure',
        sourceName: 'web.super_admin.users.send_user_password_reset',
        targetUserId: target.id,
        metadata: {
          error: securityErrorToMetadata(err),
        },
      });
      window.alert(message);
    } finally {
      setSendingPasswordResetUserId(null);
    }
  };

  const handleLinkAccounts = async () => {
    if (!canLinkAccounts) return;
    if (!linkPrimaryId || !linkSecondaryId) {
      setLinkError('Pick both a primary and a secondary account.');
      logUsersSecurityEvent({
        eventFamily: 'admin_action',
        eventType: 'link_user_accounts',
        severity: 'warning',
        outcome: 'blocked',
        sourceName: 'web.super_admin.users.link_user_accounts',
        metadata: { reason: 'missing_selection' },
      });
      return;
    }
    if (linkPrimaryId === linkSecondaryId) {
      setLinkError('Primary and secondary accounts must be different.');
      logUsersSecurityEvent({
        eventFamily: 'admin_action',
        eventType: 'link_user_accounts',
        severity: 'warning',
        outcome: 'blocked',
        sourceName: 'web.super_admin.users.link_user_accounts',
        targetUserId: linkPrimaryId,
        metadata: { reason: 'same_user_selected' },
      });
      return;
    }
    const confirmed = window.confirm(
      'Link these accounts? The secondary account will be deactivated and its household/kids data moved to the primary.'
    );
    if (!confirmed) return;

    try {
      setLinkSaving(true);
      setLinkError(null);
      const { data: response, error: linkError } = await supabase.functions.invoke('link-user-accounts', {
        body: {
          primary_user_id: linkPrimaryId,
          secondary_user_id: linkSecondaryId,
          transfer_phone: linkTransferContact,
          transfer_email: linkTransferContact,
          deactivate_secondary: linkDeactivateSecondary,
        },
      });
      if (linkError) throw linkError;
      if (response && (response as { error?: string }).error) {
        throw new Error((response as { error?: string }).error);
      }
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      showToast('Accounts linked.');
      setShowLinkModal(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to link accounts.';
      logUsersSecurityEvent({
        eventFamily: 'admin_action',
        eventType: 'link_user_accounts',
        severity: 'warning',
        outcome: 'failure',
        sourceName: 'web.super_admin.users.link_user_accounts',
        targetUserId: linkSecondaryId,
        metadata: {
          primaryUserId: linkPrimaryId,
          secondaryUserId: linkSecondaryId,
          error: securityErrorToMetadata(err),
        },
      });
      setLinkError(message);
    } finally {
      setLinkSaving(false);
    }
  };

  useEffect(() => {
    if (!showUploadModal) {
      setCsvFileName(null);
      setCsvRows([]);
      setCsvErrors([]);
      setCsvParsing(false);
      setCsvUploading(false);
      setCsvResult(null);
    }
  }, [showUploadModal]);

  const handleCsvFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvFileName(file.name);
    setCsvParsing(true);
    setCsvResult(null);

    const reader = new FileReader();
    reader.onload = () => {
      const raw = typeof reader.result === 'string' ? reader.result : '';
      const result = buildCsvRows(raw);
      setCsvRows(result.rows);
      setCsvErrors(result.errors);
      setCsvParsing(false);
    };
    reader.onerror = () => {
      setCsvRows([]);
      setCsvErrors(['Failed to read the CSV file.']);
      setCsvParsing(false);
    };
    reader.readAsText(file);
    event.target.value = '';
  }, []);

  const openCsvPicker = useCallback(() => {
    csvInputRef.current?.click();
  }, []);

  const handleBulkProvision = useCallback(async () => {
    if (!csvRows.length || csvErrors.length) return;
    setCsvUploading(true);
    setCsvResult(null);
    try {
      const payload = csvRows.map((row) => ({
        full_name: row.fullName,
        email: row.email,
        phone: row.phone,
        member_since: row.memberSince,
        household_address: row.householdAddress,
        row_number: row.rowNumber,
      }));
      const { data, error } = await supabase.functions.invoke('bulk-provision-users', {
        body: { rows: payload },
      });
      if (error) throw error;
      const result = (data as BulkProvisionResult) ?? {
        created: 0,
        skipped: 0,
        failed: 0,
      };
      setCsvResult(result);
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      showToast('Bulk provisioning complete.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Bulk provisioning failed.';
      logUsersSecurityEvent({
        eventFamily: 'admin_action',
        eventType: 'bulk_provision_users',
        severity: 'warning',
        outcome: 'failure',
        sourceName: 'web.super_admin.users.bulk_provision_users',
        metadata: {
          rowCount: csvRows.length,
          error: securityErrorToMetadata(err),
        },
      });
      setCsvErrors([message]);
    } finally {
      setCsvUploading(false);
    }
  }, [csvErrors.length, csvRows, logUsersSecurityEvent, queryClient, showToast]);

  return (
    <Page>
      {toastMessage ? (
        <ToastRegion role="status" aria-live="polite">
          <Toast>{toastMessage}</Toast>
        </ToastRegion>
      ) : null}
      <HeaderRow>
        <HeaderTitle>Users & Roles</HeaderTitle>
        <HeaderActions>
          {hasOverdueUsers && <MIAAlert>There are users MIA. Reach out to them.</MIAAlert>}
          <MessagesLink to="/admin/users/messages">
            Go to messages
            {adminUnreadCount > 0 ? <MessagesPill>{adminUnreadCount}</MessagesPill> : null}
          </MessagesLink>
          {canBulkProvision && (
            <LegendButton type="button" onClick={() => setShowUploadModal(true)}>
              Upload CSV
            </LegendButton>
          )}
          <LegendButton type="button" onClick={() => setLegendOpen(true)}>
            Role legend
            <LegendIcon>?</LegendIcon>
          </LegendButton>
          
          <MenuWrap ref={menuRef}>
            <MenuButton type="button" onClick={() => setMenuOpen((open) => !open)}>
              ...
            </MenuButton>
            {menuOpen && (
              <MenuPopover>
                <MenuItem
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setShowVisitGapModal(true);
                  }}
                  disabled={!canManageVisitGap}
                  $disabled={!canManageVisitGap}
                >
                  Visit Gap
                </MenuItem>
                <MenuItem
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setShowCampusesModal(true);
                  }}
                  disabled={!canManageVisitGap}
                  $disabled={!canManageVisitGap}
                >
                  Campus Coordinates
                </MenuItem>
                <MenuItem
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setShowLinkModal(true);
                  }}
                  disabled={!canLinkAccounts}
                  $disabled={!canLinkAccounts}
                >
                  Link Accounts
                </MenuItem>
              </MenuPopover>
            )}
          </MenuWrap>
        </HeaderActions>
      </HeaderRow>
      {isLoading && <p>Loading users…</p>}
      {error && <p>Error loading users</p>}
      <>
        <ControlsRow>
          <TextInput
            placeholder="Search by name, email, or phone"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
            <option value="all">All roles</option>
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {getRoleLabel(role)}
              </option>
            ))}
          </Select>
          <Select
            value={gapFilter}
            onChange={(event) => setGapFilter(event.target.value as 'all' | 'overdue')}
          >
            <option value="all">All attendance</option>
            <option value="overdue">Over visit gap</option>
          </Select>
          <OnlineToggle type="button" $active={onlineOnly} onClick={() => setOnlineOnly((prev) => !prev)}>
            {onlineOnly ? 'Online: On' : 'Online: Off'}
          </OnlineToggle>
          <OnlineToggle type="button" $active={recentSort} onClick={() => setRecentSort((prev) => !prev)} title="Sort by last active (latest first)">
            {recentSort ? 'Recent: On' : 'Recent: Off'}
          </OnlineToggle>
        </ControlsRow>
        {canManageRoles && selectedUserIds.length > 0 && (
          <BulkActionStrip>
            <BulkActionText>
              {selectedUserIds.length} user{selectedUserIds.length === 1 ? '' : 's'} selected on this page
            </BulkActionText>
            <ActionRow>
              <ActionButton type="button" $variant="primary" onClick={openBulkRoleEditor} disabled={bulkRoleSaving}>
                Bulk edit roles
              </ActionButton>
              <ActionButton
                type="button"
                $variant="ghost"
                onClick={() => setSelectedUserIds([])}
                disabled={bulkRoleSaving}
              >
                Clear selection
              </ActionButton>
              {canSendPushNotifications && (
                <ActionButton
                  type="button"
                  $variant="primary"
                  onClick={() => openPushComposer(selectedUserIds)}
                  disabled={pushSubmitting}
                >
                  Send push
                </ActionButton>
              )}
            </ActionRow>
          </BulkActionStrip>
        )}
        <TableCard>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  {canManageRoles && (
                    <RowSelectHeader>
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allSelectableSelected}
                        onChange={toggleSelectAllVisible}
                        disabled={selectableUsers.length === 0 || bulkRoleSaving}
                        aria-label="Select all visible users"
                      />
                    </RowSelectHeader>
                  )}
                  <th></th>
                  <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Platform</th>
                <th>Version</th>
                <th>Roles</th>
                <th>Joined</th>
                {(canManageRoles || canDeleteUsers || canMessageUsers || canEditUserContact || canManageUserPasswords || canSendPushNotifications) && (
                  <th>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const partnerVerified = partnerRequestStatusByUser.get(u.id);
                const isNewPartner = partnerVerified !== undefined;
                const isOnline = isUserOnline(u.last_active_at ?? null);
                return (
                  <tr
                    key={u.id}
                    style={
                      (() => {
                        const lastVisit = lastVisitByUser[u.id];
                        if (!lastVisit) return undefined;
                        const lastVisitMs = new Date(lastVisit).getTime();
                        if (!Number.isFinite(lastVisitMs)) return undefined;
                        const gapDays = Number.isFinite(visitGapDays) && visitGapDays > 0 ? visitGapDays : 60;
                        const gapMs = gapDays * 24 * 60 * 60 * 1000;
                        return Date.now() - lastVisitMs > gapMs ? { background: 'var(--errorContainer)' } : undefined;
                      })()
                    }
                  >
                    {canManageRoles && (
                      <RowSelectCell>
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(u.id)}
                          onChange={() => toggleUserSelection(u.id)}
                          disabled={u.id === user?.id || bulkRoleSaving}
                          aria-label={`Select ${u.full_name || 'user'}`}
                        />
                      </RowSelectCell>
                    )}
                    <td>
                      <AvatarWrap>
                        <AvatarCell>
                          {u.avatar ? (
                            <AvatarImage src={u.avatar} alt={u.full_name} />
                          ) : (
                            (u.full_name || 'U').charAt(0).toUpperCase()
                          )}
                        </AvatarCell>
                        {isOnline && <OnlineDot title="Online" />}
                      </AvatarWrap>
                    </td>
                    <td>
                      <NameLink to={`/admin/members/${u.id}`}>{u.full_name}</NameLink>
                    </td>
                    <td>{u.email ?? '-'}</td>
                    <td>{u.phone ?? '-'}</td>
                    <td>{u.membership_status ?? 'visitor'}</td>
                    <td>{u.platform ?? '-'}</td>
                    <td>{u.app_version ?? '-'}</td>
                    <td>
                      {editingUserId === u.id && canManageRoles ? (
                        <RoleEditor>
                          {ROLE_OPTIONS.map((role) => (
                            <RoleToggle key={role.value}>
                              <input
                                type="checkbox"
                                checked={draftRoles.includes(role.value)}
                                disabled={!canEditManagedRole(role.value)}
                                onChange={() => toggleRole(role.value)}
                              />
                              {role.label}
                            </RoleToggle>
                          ))}
                        </RoleEditor>
                      ) : u.roles?.length ? (
                        <>
                          {isNewPartner && (
                            <NewPartnerBadge $verified={partnerVerified}>New Partner</NewPartnerBadge>
                          )}
                          {u.roles.map((r) => (
                            <RoleBadge key={r}>{getRoleLabel(r)}</RoleBadge>
                          ))}
                        </>
                      ) : (
                        <>
                          {isNewPartner && (
                            <NewPartnerBadge $verified={partnerVerified}>New Partner</NewPartnerBadge>
                          )}
                          visitor
                        </>
                      )}
                    </td>
                    <td>{u.member_since ? new Date(u.member_since).toLocaleDateString() : '-'}</td>
                    {(canManageRoles || canDeleteUsers || canMessageUsers || canEditUserContact || canManageUserPasswords || canSendPushNotifications) && (
                      <td>
                        {editingUserId === u.id ? (
                          <ActionRow>
                            {canManageRoles && (
                              <ActionButton
                                $variant="primary"
                                disabled={savingUserId === u.id}
                                onClick={() => saveRoles(u.id)}
                              >
                                {savingUserId === u.id ? 'Saving...' : 'Save'}
                              </ActionButton>
                            )}
                            <ActionButton $variant="ghost" onClick={cancelEditing}>
                              Cancel
                            </ActionButton>
                          </ActionRow>
                        ) : (
                          <MenuWrap ref={actionMenuUserId === u.id ? actionMenuRef : null}>
                            <MenuButton
                              type="button"
                              onClick={() =>
                                setActionMenuUserId((current) => (current === u.id ? null : u.id))
                              }
                              aria-label="Open actions menu"
                            >
                              ⋯
                            </MenuButton>
                            {actionMenuUserId === u.id && (
                              <MenuPopover>
                                {canMessageUsers && (
                                  <MenuItem
                                    type="button"
                                    disabled={user?.id === u.id}
                                    title={user?.id === u.id ? 'You cannot message yourself.' : 'Send message'}
                                    onClick={() => {
                                      setActionMenuUserId(null);
                                      navigate(`/admin/users/messages?recipient=${u.id}`);
                                    }}
                                  >
                                    Message
                                  </MenuItem>
                                )}
                                {canSendPushNotifications && (
                                  <MenuItem
                                    type="button"
                                    onClick={() => {
                                      setActionMenuUserId(null);
                                      openPushComposer([u.id]);
                                    }}
                                  >
                                    Send push
                                  </MenuItem>
                                )}
                                {canManageRoles && (
                                  <MenuItem
                                    type="button"
                                    onClick={() => {
                                      setActionMenuUserId(null);
                                      startEditing(u);
                                    }}
                                  >
                                    Edit roles
                                  </MenuItem>
                                )}
                                {canEditUserContact && (
                                  <MenuItem
                                    type="button"
                                    onClick={() => {
                                      openContactEditor(u, 'email');
                                    }}
                                  >
                                    Edit email
                                  </MenuItem>
                                )}
                                {canEditUserContact && (
                                  <MenuItem
                                    type="button"
                                    onClick={() => {
                                      openContactEditor(u, 'phone');
                                    }}
                                  >
                                    Edit phone
                                  </MenuItem>
                                )}
                                {canManageUserPasswords && (
                                  <MenuItem
                                    type="button"
                                    onClick={() => {
                                      openPasswordModal(u);
                                    }}
                                  >
                                    Set password
                                  </MenuItem>
                                )}
                                {canManageUserPasswords && (
                                  <MenuItem
                                    type="button"
                                    disabled={sendingPasswordResetUserId === u.id}
                                    onClick={() => {
                                      setActionMenuUserId(null);
                                      handleSendPasswordReset(u);
                                    }}
                                  >
                                    {sendingPasswordResetUserId === u.id ? 'Sending...' : 'Send password reset'}
                                  </MenuItem>
                                )}
                                {canDeleteUsers && (
                                  <MenuItem
                                    type="button"
                                    disabled={deletingUserId === u.id || user?.id === u.id}
                                    title={user?.id === u.id ? 'You cannot delete your own account.' : 'Delete user'}
                                    onClick={() => {
                                      setActionMenuUserId(null);
                                      handleDeleteUser(u);
                                    }}
                                  >
                                    {deletingUserId === u.id ? 'Deleting...' : 'Delete'}
                                  </MenuItem>
                                )}
                              </MenuPopover>
                            )}
                          </MenuWrap>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            </Table>
          </TableWrap>
          <TableFooter>
            <span>
              {totalCount === 0
                ? 'Showing 0 of 0'
                : `Showing ${(currentPage - 1) * PAGE_SIZE + 1}-${Math.min(
                    currentPage * PAGE_SIZE,
                    totalCount
                  )} of ${totalCount}`}
            </span>
            {totalPages > 1 && (
              <Pagination>
                <PageButton
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  {'<'}
                </PageButton>
                {pageIndicators.map((indicator, index) =>
                  indicator === 'ellipsis' ? (
                    <PageEllipsis key={`ellipsis-${index}`}>...</PageEllipsis>
                  ) : (
                    <PageButton
                      key={indicator}
                      type="button"
                      $active={indicator === currentPage}
                      onClick={() => setCurrentPage(indicator)}
                      aria-label={`Page ${indicator}`}
                    >
                      {indicator}
                    </PageButton>
                  )
                )}
                <PageButton
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  {'>'}
                </PageButton>
              </Pagination>
            )}
          </TableFooter>
        </TableCard>
      </>
      {showBulkRoleModal && (
        <ModalOverlay onClick={closeBulkRoleEditor}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Bulk edit roles</ModalTitle>
              <ModalCloseButton type="button" onClick={closeBulkRoleEditor} disabled={bulkRoleSaving}>
                X
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <HelperText>
                Apply role additions/removals to {selectedUserIds.length} selected user
                {selectedUserIds.length === 1 ? '' : 's'} on this page.
              </HelperText>
              <BulkRoleGrid style={{ marginTop: '1rem' }}>
                <BulkRoleColumn>
                  <BulkRoleTitle>Roles to add</BulkRoleTitle>
                  {ROLE_OPTIONS.map((role) => (
                    <RoleToggle key={`bulk-add-${role.value}`}>
                      <input
                        type="checkbox"
                        checked={bulkAddRoles.includes(role.value)}
                        onChange={() => toggleBulkAddRole(role.value)}
                        disabled={bulkRoleSaving || !canEditManagedRole(role.value)}
                      />
                      {role.label}
                    </RoleToggle>
                  ))}
                </BulkRoleColumn>
                <BulkRoleColumn>
                  <BulkRoleTitle>Roles to remove</BulkRoleTitle>
                  {ROLE_OPTIONS.map((role) => (
                    <RoleToggle key={`bulk-remove-${role.value}`}>
                      <input
                        type="checkbox"
                        checked={bulkRemoveRoles.includes(role.value)}
                        onChange={() => toggleBulkRemoveRole(role.value)}
                        disabled={bulkRoleSaving || !canEditManagedRole(role.value)}
                      />
                      {role.label}
                    </RoleToggle>
                  ))}
                </BulkRoleColumn>
              </BulkRoleGrid>
              <HelperText style={{ marginTop: '0.9rem' }}>
                Role changes are additive/subtractive. Existing roles not selected here stay unchanged.
              </HelperText>
              {!hasBulkRoleChanges && (
                <HelperText style={{ marginTop: '0.45rem', color: 'var(--error)' }}>
                  Select at least one role to add or remove.
                </HelperText>
              )}
              <ActionRow style={{ marginTop: '1rem' }}>
                <ActionButton
                  type="button"
                  $variant="primary"
                  onClick={applyBulkRoleChanges}
                  disabled={!hasBulkRoleChanges || bulkRoleSaving}
                >
                  {bulkRoleSaving ? 'Saving...' : 'Apply changes'}
                </ActionButton>
                <ActionButton type="button" $variant="ghost" onClick={closeBulkRoleEditor} disabled={bulkRoleSaving}>
                  Cancel
                </ActionButton>
              </ActionRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
      {legendOpen && (
        <ModalOverlay onClick={() => setLegendOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Role access legend</ModalTitle>
              <ModalCloseButton type="button" onClick={() => setLegendOpen(false)}>
                ✕
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <LegendGrid>
                {ROLE_OPTIONS.map((role) => {
                  const permissions = ROLE_PERMISSIONS[role.value] ?? [];
                  return (
                    <LegendRoleCard key={role.value}>
                      <LegendRoleTitle>{role.label}</LegendRoleTitle>
                      {permissions.length ? (
                        <LegendList>
                          {permissions.map((permission) => (
                            <li key={permission}>{permissionLabels[permission]}</li>
                          ))}
                        </LegendList>
                      ) : (
                        <LegendEmpty>Public access only.</LegendEmpty>
                      )}
                    </LegendRoleCard>
                  );
                })}
              </LegendGrid>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
      {showVisitGapModal && (
        <ModalOverlay onClick={() => setShowVisitGapModal(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Campus visit gap</ModalTitle>
              <ModalCloseButton type="button" onClick={() => setShowVisitGapModal(false)}>
                ✕
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <HelperText>Show inactivity notice after this many days without a campus visit.</HelperText>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '0.75rem' }}>
                <TextInput
                  type="number"
                  min="1"
                  value={String(visitGapDays)}
                  onChange={(event) => setVisitGapDays(Number(event.target.value))}
                />
                <ActionButton $variant="primary" type="button" onClick={handleSaveVisitGap} disabled={visitGapSaving}>
                  {visitGapSaving ? 'Saving...' : 'Save'}
                </ActionButton>
              </div>
              {visitGapError ? <HelperText style={{ color: 'var(--error)', marginTop: '0.5rem' }}>{visitGapError}</HelperText> : null}
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
      {showCampusesModal && (
        <ModalOverlay onClick={() => setShowCampusesModal(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Campus Coordinates</ModalTitle>
              <ModalCloseButton type="button" onClick={() => setShowCampusesModal(false)}>
                ✕
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <HelperText>Set latitude/longitude for each campus.</HelperText>
              <CampusGrid>
                {campuses.length === 0 && <HelperText>No campuses yet.</HelperText>}
                {campuses.map((campus) => (
                  <CampusRow key={campus.id}>
                    <TextInput
                      value={campus.name}
                      onChange={(event) =>
                        setCampuses((prev) =>
                          prev.map((item) => (item.id === campus.id ? { ...item, name: event.target.value } : item))
                        )
                      }
                    />
                    <TextInput
                      value={String(campus.latitude)}
                      onChange={(event) =>
                        setCampuses((prev) =>
                          prev.map((item) =>
                            item.id === campus.id ? { ...item, latitude: Number(event.target.value) } : item
                          )
                        )
                      }
                    />
                    <TextInput
                      value={String(campus.longitude)}
                      onChange={(event) =>
                        setCampuses((prev) =>
                          prev.map((item) =>
                            item.id === campus.id ? { ...item, longitude: Number(event.target.value) } : item
                          )
                        )
                      }
                    />
                    <TextInput
                      value={String(campus.radius_meters)}
                      onChange={(event) =>
                        setCampuses((prev) =>
                          prev.map((item) =>
                            item.id === campus.id ? { ...item, radius_meters: Number(event.target.value) } : item
                          )
                        )
                      }
                    />
                    <ActionButton $variant="primary" type="button" onClick={() => handleSaveCampus(campus.id)}>
                      Save
                    </ActionButton>
                  </CampusRow>
                ))}
              </CampusGrid>
              <CampusRow style={{ marginTop: '1rem' }}>
                <TextInput
                  placeholder="Campus name"
                  value={campusForm.name}
                  onChange={(event) => setCampusForm({ ...campusForm, name: event.target.value })}
                />
                <TextInput
                  placeholder="Latitude"
                  value={campusForm.latitude}
                  onChange={(event) => setCampusForm({ ...campusForm, latitude: event.target.value })}
                />
                <TextInput
                  placeholder="Longitude"
                  value={campusForm.longitude}
                  onChange={(event) => setCampusForm({ ...campusForm, longitude: event.target.value })}
                />
                <TextInput
                  placeholder="Radius"
                  value={campusForm.radius_meters}
                  onChange={(event) => setCampusForm({ ...campusForm, radius_meters: event.target.value })}
                />
                <ActionButton $variant="primary" type="button" onClick={handleAddCampus}>
                  Add
                </ActionButton>
              </CampusRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
      {showLinkModal && (
        <ModalOverlay onClick={() => setShowLinkModal(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Link accounts</ModalTitle>
              <ModalCloseButton type="button" onClick={() => setShowLinkModal(false)}>
                ✕
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <HelperText>
                Choose the primary account to keep. The secondary account will be merged into it so the household
                child record stays connected.
              </HelperText>
              <LinkGrid>
                <LinkColumn>
                  <Label>Primary account (keep)</Label>
                  <TextInput
                    placeholder="Search by name, email, or phone"
                    value={linkPrimarySearch}
                    onChange={(event) => setLinkPrimarySearch(event.target.value)}
                  />
                  <Select value={linkPrimaryId} onChange={(event) => setLinkPrimaryId(event.target.value)}>
                    <option value="">Select primary account</option>
                    {primaryLinkOptions.map((row) => (
                      <option key={row.id} value={row.id}>
                        {getUserLabel(row)}
                      </option>
                    ))}
                  </Select>
                  {selectedPrimary ? (
                    <LinkCard>
                      <div>{selectedPrimary.full_name || 'Unnamed member'}</div>
                      <HelperText>{selectedPrimary.email || selectedPrimary.phone || 'No contact info'}</HelperText>
                    </LinkCard>
                  ) : null}
                </LinkColumn>
                <LinkColumn>
                  <Label>Secondary account (merge)</Label>
                  <TextInput
                    placeholder="Search by name, email, or phone"
                    value={linkSecondarySearch}
                    onChange={(event) => setLinkSecondarySearch(event.target.value)}
                  />
                  <Select value={linkSecondaryId} onChange={(event) => setLinkSecondaryId(event.target.value)}>
                    <option value="">Select secondary account</option>
                    {secondaryLinkOptions.map((row) => (
                      <option key={row.id} value={row.id}>
                        {getUserLabel(row)}
                      </option>
                    ))}
                  </Select>
                  {selectedSecondary ? (
                    <LinkCard>
                      <div>{selectedSecondary.full_name || 'Unnamed member'}</div>
                      <HelperText>{selectedSecondary.email || selectedSecondary.phone || 'No contact info'}</HelperText>
                    </LinkCard>
                  ) : null}
                </LinkColumn>
              </LinkGrid>
              <div style={{ marginTop: '1rem', display: 'grid', gap: '0.5rem' }}>
                <CheckboxRow>
                  <input
                    type="checkbox"
                    checked={linkTransferContact}
                    onChange={(event) => setLinkTransferContact(event.target.checked)}
                  />
                  Copy missing phone/email from secondary to primary.
                </CheckboxRow>
                <CheckboxRow>
                  <input
                    type="checkbox"
                    checked={linkDeactivateSecondary}
                    onChange={(event) => setLinkDeactivateSecondary(event.target.checked)}
                  />
                  Deactivate the secondary account after linking.
                </CheckboxRow>
              </div>
              {linkError ? <HelperText style={{ color: 'var(--error)', marginTop: '0.5rem' }}>{linkError}</HelperText> : null}
              <ActionRow style={{ marginTop: '1rem' }}>
                <ActionButton
                  $variant="primary"
                  type="button"
                  onClick={handleLinkAccounts}
                  disabled={linkSaving || !linkPrimaryId || !linkSecondaryId}
                >
                  {linkSaving ? 'Linking...' : 'Link accounts'}
                </ActionButton>
                <ActionButton type="button" $variant="ghost" onClick={() => setShowLinkModal(false)}>
                  Cancel
                </ActionButton>
              </ActionRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
      {showUploadModal && (
        <ModalOverlay onClick={() => setShowUploadModal(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Bulk provision users</ModalTitle>
              <ModalCloseButton type="button" onClick={() => setShowUploadModal(false)}>
                ✕
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <HelperText>
                Upload a CSV with columns: Name, Phone, Email, Member Since, Household Address.
              </HelperText>
              <UploadCard style={{ marginTop: '0.85rem' }}>
                <UploadRow>
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    style={{ display: 'none' }}
                    onChange={handleCsvFileChange}
                  />
                  <ActionButton type="button" $variant="primary" onClick={openCsvPicker}>
                    Choose CSV
                  </ActionButton>
                  {csvFileName ? <UploadFileName>{csvFileName}</UploadFileName> : null}
                </UploadRow>
                {csvParsing ? <HelperText>Parsing CSV...</HelperText> : null}
                {csvRows.length > 0 ? (
                  <HelperText>Parsed {csvRows.length} rows ready for provisioning.</HelperText>
                ) : null}
                {csvErrors.length > 0 && (
                  <UploadErrorList>
                    {csvErrors.slice(0, 6).map((err, index) => (
                      <li key={`${err}-${index}`}>{err}</li>
                    ))}
                  </UploadErrorList>
                )}
              </UploadCard>

              {csvRows.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <Label>Preview (first 5 rows)</Label>
                  <UploadPreviewTable style={{ marginTop: '0.5rem' }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Member Since</th>
                        <th>Household Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvRows.slice(0, 5).map((row) => (
                        <tr key={row.rowNumber}>
                          <td>{row.fullName}</td>
                          <td>{row.email ?? '-'}</td>
                          <td>{row.phone ?? '-'}</td>
                          <td>{row.memberSince ?? '-'}</td>
                          <td>{row.householdAddress ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </UploadPreviewTable>
                </div>
              )}

              {csvResult && (
                <div style={{ marginTop: '1rem' }}>
                  <HelperText>
                    Created: {csvResult.created} | Skipped: {csvResult.skipped} | Failed: {csvResult.failed}
                  </HelperText>
                  {csvResult.errors?.length ? (
                    <UploadErrorList>
                      {csvResult.errors.slice(0, 6).map((err) => (
                        <li key={`${err.row}-${err.message}`}>
                          Row {err.row}: {err.message}
                        </li>
                      ))}
                    </UploadErrorList>
                  ) : null}
                </div>
              )}

              <ActionRow style={{ marginTop: '1rem' }}>
                <ActionButton
                  $variant="primary"
                  type="button"
                  onClick={handleBulkProvision}
                  disabled={csvUploading || csvErrors.length > 0 || csvRows.length === 0}
                >
                  {csvUploading ? 'Uploading...' : 'Provision Users'}
                </ActionButton>
                <ActionButton type="button" $variant="ghost" onClick={() => setShowUploadModal(false)}>
                  Cancel
                </ActionButton>
              </ActionRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
      {showPushModal && (
        <ModalOverlay onClick={() => (pushSubmitting ? null : resetPushComposer())}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Send push notification</ModalTitle>
              <ModalCloseButton type="button" onClick={resetPushComposer} disabled={pushSubmitting}>
                X
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <HelperText>
                Sending to {pushRecipients.length} visible user{pushRecipients.length === 1 ? '' : 's'}.
              </HelperText>
              {pushRecipients.length > 0 ? (
                <HelperText style={{ marginTop: '0.4rem' }}>
                  {pushRecipients
                    .slice(0, 4)
                    .map((recipient) => recipient?.full_name || recipient?.email || recipient?.phone || 'Unnamed user')
                    .join(', ')}
                  {pushRecipients.length > 4 ? ` +${pushRecipients.length - 4} more` : ''}
                </HelperText>
              ) : null}
              <div style={{ marginTop: '0.85rem', display: 'grid', gap: '0.55rem' }}>
                <Label>Title</Label>
                <TextInput
                  value={pushTitle}
                  onChange={(event) => setPushTitle(event.target.value)}
                  maxLength={120}
                  placeholder="Enter push title"
                />
                <Label>Message</Label>
                <TextArea
                  value={pushMessage}
                  onChange={(event) => setPushMessage(event.target.value)}
                  maxLength={280}
                  placeholder="Enter push message"
                />
                <Label>Landing destination</Label>
                <Select
                  value={pushDestination}
                  onChange={(event) => setPushDestination(event.target.value as PushDestination)}
                >
                  {PUSH_DESTINATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                {pushDestination === 'events' ? (
                  <>
                    <Label>Event ID (optional)</Label>
                    <TextInput
                      value={pushEventId}
                      onChange={(event) => setPushEventId(event.target.value)}
                      placeholder="event-uuid"
                    />
                  </>
                ) : null}
                {pushDestination === 'transportation' ? (
                  <>
                    <Label>Transportation status</Label>
                    <Select
                      value={pushRideStatus}
                      onChange={(event) => setPushRideStatus(event.target.value as PushRideStatus)}
                    >
                      {PUSH_RIDE_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </>
                ) : null}
                {pushError ? <HelperText style={{ color: 'var(--error)' }}>{pushError}</HelperText> : null}
              </div>
              <ActionRow style={{ marginTop: '1rem' }}>
                <ActionButton type="button" $variant="primary" onClick={handleSendPush} disabled={pushSubmitting}>
                  {pushSubmitting ? 'Sending...' : 'Send push'}
                </ActionButton>
                <ActionButton type="button" $variant="ghost" onClick={resetPushComposer} disabled={pushSubmitting}>
                  Cancel
                </ActionButton>
              </ActionRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
      {contactEditTarget && (
        <ModalOverlay onClick={closeContactEditModal}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{contactEditMode === 'email' ? 'Edit user email' : 'Edit user phone'}</ModalTitle>
              <ModalCloseButton type="button" onClick={closeContactEditModal}>
                ✕
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <HelperText>
                Updating{' '}
                {contactEditTarget.full_name || contactEditTarget.email || contactEditTarget.phone || 'selected user'}
                .
              </HelperText>
              <div style={{ marginTop: '0.85rem', display: 'grid', gap: '0.5rem' }}>
                <Label>{contactEditMode === 'email' ? 'Email address' : 'Phone number'}</Label>
                <TextInput
                  type={contactEditMode === 'email' ? 'email' : 'tel'}
                  placeholder={contactEditMode === 'email' ? 'name@example.com' : '+1234567890'}
                  value={contactEditValue}
                  onChange={(event) => setContactEditValue(event.target.value)}
                />
                {contactEditError ? <HelperText style={{ color: 'var(--error)' }}>{contactEditError}</HelperText> : null}
              </div>
              <ActionRow style={{ marginTop: '1rem' }}>
                <ActionButton type="button" $variant="primary" onClick={handleSaveContact} disabled={contactEditSaving}>
                  {contactEditSaving ? 'Saving...' : 'Save'}
                </ActionButton>
                <ActionButton type="button" $variant="ghost" onClick={closeContactEditModal}>
                  Cancel
                </ActionButton>
              </ActionRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
      {passwordEditTarget && (
        <ModalOverlay onClick={closePasswordModal}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Set user password</ModalTitle>
              <ModalCloseButton type="button" onClick={closePasswordModal}>
                ✕
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <HelperText>
                Set a temporary password for{' '}
                {passwordEditTarget.full_name || passwordEditTarget.email || passwordEditTarget.phone || 'selected user'}
                .
              </HelperText>
              <div style={{ marginTop: '0.85rem', display: 'grid', gap: '0.5rem' }}>
                <Label>New password</Label>
                <TextInput
                  type="password"
                  placeholder="Enter temporary password"
                  value={passwordDraft}
                  onChange={(event) => setPasswordDraft(event.target.value)}
                />
                <Label>Confirm password</Label>
                <TextInput
                  type="password"
                  placeholder="Re-enter password"
                  value={passwordConfirmDraft}
                  onChange={(event) => setPasswordConfirmDraft(event.target.value)}
                />
                <HelperText>
                  Must be at least 10 characters and include uppercase, lowercase, number, and symbol.
                </HelperText>
                {passwordEditError ? <HelperText style={{ color: 'var(--error)' }}>{passwordEditError}</HelperText> : null}
              </div>
              <ActionRow style={{ marginTop: '1rem' }}>
                <ActionButton type="button" $variant="primary" onClick={handleSavePassword} disabled={passwordEditSaving}>
                  {passwordEditSaving ? 'Saving...' : 'Save password'}
                </ActionButton>
                <ActionButton type="button" $variant="ghost" onClick={closePasswordModal}>
                  Cancel
                </ActionButton>
              </ActionRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default UsersPage;
